import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Search, AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { leadAutomationService, type AutomationLog } from '../services/leadAutomation.service';
import { mailCampaignsService } from '../services/mail-campaigns.service';
import { DocugenLifecycleBadge } from '../components/crm/DocugenLifecycleBadge';

interface CampaignRecipient {
  id: number;
  name: string;
  email: string;
  status: 'PENDING' | 'SENT' | 'FAILED';
  sentAt: string | null;
  bouncedAt: string | null;
  errorMessage: string | null;
  aiCategory: string | null;
  aiSeverity: 'PERMANENT' | 'TEMPORARY' | null;
  aiAction: string | null;
  aiSuggestedEmail: string | null;
  aiAnalyzedAt: string | null;
}

interface CampaignDetail {
  id: number;
  totalRecipients: number;
  totalSent: number;
  totalFailed: number;
  totalBounced: number;
  recipients: CampaignRecipient[];
}

const LIFECYCLE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todas as categorias' },
  { value: 'UNCATEGORIZED', label: 'Sem categoria' },
  { value: 'NEW_ACCOUNT', label: 'Conta criada' },
  { value: 'FIRST_DOCUMENT', label: 'Primeiro documento' },
  { value: 'ACTIVE_USER', label: 'Usuário ativo' },
  { value: 'TRIED_LOCKED', label: 'Tentou premium' },
  { value: 'HEAVY_USAGE', label: 'Uso intenso' },
  { value: 'REACHED_LIMIT', label: 'Atingiu limite' },
  { value: 'INACTIVE', label: 'Inativo' },
];

function formatDetail(l: AutomationLog): string {
  if (l.skipDetail) {
    return l.skipDetail;
  }
  if (l.result === 'MOVED' && (l.fromStage || l.toStage)) {
    const from = l.fromStage?.name ?? '—';
    const to = l.toStage?.name ?? '—';
    return `${from} → ${to}`;
  }
  if (l.result === 'SKIPPED_NO_LEAD') {
    return 'destinatário sem lead vinculado';
  }
  if (l.result === 'SKIPPED_ALREADY_AT_TARGET') {
    return 'lead já está na coluna alvo';
  }
  if (l.result === 'SKIPPED_NO_TARGET_CONFIG') {
    return 'produto sem coluna alvo configurada';
  }
  if (l.result === 'SKIPPED_TEMPLATE_FLAG') {
    return 'template marcado para não mover lead';
  }
  return '';
}

export default function MailCampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [lifecycleStage, setLifecycleStage] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [analyzingBounces, setAnalyzingBounces] = useState(false);

  const reload = useCallback(async () => {
    const [autoData, campaignData] = await Promise.all([
      leadAutomationService.getLogs(campaignId, {
        lifecycleStage: lifecycleStage || undefined,
      }),
      mailCampaignsService.getById(campaignId),
    ]);
    setSummary(autoData.summary);
    setLogs(autoData.logs);
    setDetail(campaignData);
  }, [campaignId, lifecycleStage]);

  useEffect(() => {
    reload();
  }, [reload]);

  const bouncedRecipients = (detail?.recipients ?? []).filter((r) => r.bouncedAt);
  // Opt-out recipients are stored as FAILED with a sentinel errorMessage — they
  // are deliberate skips, not delivery failures, so keep them out of the failed list.
  const failedRecipients = (detail?.recipients ?? []).filter(
    (r) => r.status === 'FAILED' && !r.bouncedAt && r.errorMessage !== 'Opt-out de marketing'
  );

  async function reanalyzeBounces() {
    setAnalyzingBounces(true);
    try {
      const result = await mailCampaignsService.reanalyzeBounces(campaignId);
      if (result.analyzed > 0) {
        toast.success(`IA analisou ${result.analyzed} bounce(s)`);
      } else if (result.total === 0) {
        toast(`Nenhum bounce pendente para analisar`);
      } else {
        toast.error(`Análise falhou (${result.failed} erros). Verifique a chave em Integrações.`);
      }
      await reload();
    } catch {
      toast.error('Erro ao analisar bounces');
    } finally {
      setAnalyzingBounces(false);
    }
  }

  async function runAutomation() {
    setBusy(true);
    setMessage(null);
    try {
      const { enqueued } = await leadAutomationService.runForCampaign(campaignId);
      setMessage(`${enqueued} jobs enfileirados. Atualizando em 3s...`);
      setTimeout(reload, 3000);
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : 'Erro ao rodar automação');
    } finally {
      setBusy(false);
    }
  }

  // Client-side name search on top of server-side category filter
  const visibleLogs = nameSearch.trim()
    ? logs.filter((l) =>
        (l.lead?.name ?? '').toLowerCase().includes(nameSearch.trim().toLowerCase())
      )
    : logs;

  return (
    <div className="p-6">
      <h1 className="mb-4 text-2xl font-semibold text-ink-primary">Campanha #{campaignId}</h1>

      {detail && (
        <section className="mb-6 rounded border border-border bg-surface p-4">
          <h2 className="mb-3 text-lg font-semibold text-ink-primary">Entrega</h2>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <div className="rounded bg-canvas p-3 text-sm">
              <div className="text-xs text-ink-tertiary">Destinatários</div>
              <div className="text-xl font-semibold text-ink-primary">{detail.totalRecipients}</div>
            </div>
            <div className="rounded bg-canvas p-3 text-sm">
              <div className="text-xs text-ink-tertiary">Enviados</div>
              <div className="text-xl font-semibold text-accent">{detail.totalSent}</div>
            </div>
            <div className="rounded bg-canvas p-3 text-sm">
              <div className="text-xs text-ink-tertiary">Falhas (SMTP)</div>
              <div className="text-xl font-semibold text-danger">{detail.totalFailed}</div>
            </div>
            <div className="rounded bg-canvas p-3 text-sm">
              <div className="text-xs text-ink-tertiary flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Devolvidos (bounce)
              </div>
              <div className="text-xl font-semibold text-warning">{detail.totalBounced}</div>
            </div>
          </div>

          {(bouncedRecipients.length > 0 || failedRecipients.length > 0) && (
            <>
              <div className="mt-4 mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium text-ink-secondary">
                  Destinatários que falharam
                </h3>
                {bouncedRecipients.length > 0 && (
                  <button
                    type="button"
                    onClick={reanalyzeBounces}
                    disabled={analyzingBounces}
                    title="Analisa bounces ainda sem análise IA"
                    className="flex items-center gap-2 rounded bg-subtle px-3 py-1.5 text-xs font-medium text-ink-secondary hover:bg-subtle disabled:opacity-50 transition-colors"
                  >
                    {analyzingBounces ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    Analisar com IA
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border bg-canvas">
                      <th className="px-3 py-2 font-medium text-ink-secondary">Email</th>
                      <th className="px-3 py-2 font-medium text-ink-secondary">Nome</th>
                      <th className="px-3 py-2 font-medium text-ink-secondary">Tipo</th>
                      <th className="px-3 py-2 font-medium text-ink-secondary">Motivo</th>
                      <th className="px-3 py-2 font-medium text-ink-secondary">
                        Análise IA & ação sugerida
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {bouncedRecipients.map((r) => (
                      <BounceRow key={r.id} recipient={r} tone="warning" label="Bounce" />
                    ))}
                    {failedRecipients.map((r) => (
                      <BounceRow key={r.id} recipient={r} tone="danger" label="Falha SMTP" />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      <section className="mb-6 rounded border border-border bg-surface p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink-primary">Automação de colunas</h2>
          <button
            type="button"
            onClick={runAutomation}
            disabled={busy}
            className="rounded bg-ink-primary px-4 py-2 text-sm font-medium text-canvas hover:bg-ink-secondary transition-colors disabled:opacity-50"
          >
            {busy ? 'Enfileirando...' : 'Aplicar automação'}
          </button>
        </div>
        {message && <p className="mt-2 text-sm text-ink-secondary">{message}</p>}

        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} className="rounded bg-canvas p-2 text-sm">
              <div className="font-mono text-xs text-ink-secondary">{k}</div>
              <div className="text-lg font-semibold text-ink-primary">{v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded border border-border bg-surface p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink-primary">Últimos resultados</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary" />
              <input
                type="text"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Buscar lead..."
                className="input pl-8 text-sm w-44"
              />
            </div>
            <select
              value={lifecycleStage}
              onChange={(e) => setLifecycleStage(e.target.value)}
              className="input text-sm w-auto"
              title="Filtra leads pela categoria de lifecycle"
            >
              {LIFECYCLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {(nameSearch || lifecycleStage) && (
              <button
                type="button"
                onClick={() => {
                  setNameSearch('');
                  setLifecycleStage('');
                }}
                className="text-xs text-ink-secondary border border-border rounded px-2 py-1 hover:bg-subtle"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
        {visibleLogs.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-tertiary">
            {lifecycleStage || nameSearch
              ? 'Nenhum lead bate com esses filtros.'
              : 'Sem resultados ainda.'}
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-canvas">
                <th className="px-4 py-3 font-medium text-ink-secondary">Data</th>
                <th className="px-4 py-3 font-medium text-ink-secondary">Lead</th>
                <th className="px-4 py-3 font-medium text-ink-secondary">Categoria</th>
                <th className="px-4 py-3 font-medium text-ink-secondary">Resultado</th>
                <th className="px-4 py-3 font-medium text-ink-secondary">Source</th>
                <th className="px-4 py-3 font-medium text-ink-secondary">Detalhe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleLogs.map((l) => (
                <tr key={l.id} className="hover:bg-subtle transition-colors">
                  <td className="px-4 py-3 text-ink-secondary">
                    {new Date(l.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-ink-primary">{l.lead?.name ?? '—'}</td>
                  <td className="px-4 py-3">
                    {l.lead ? (
                      <DocugenLifecycleBadge
                        lead={{ ...l.lead, stageType: l.toStage ? null : null }}
                        variant="compact"
                      />
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-ink-secondary">{l.result}</span>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{l.source}</td>
                  <td className="px-4 py-3 text-xs text-ink-tertiary">{formatDetail(l)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function BounceRow({
  recipient: r,
  tone,
  label,
}: {
  recipient: CampaignRecipient;
  tone: 'warning' | 'danger';
  label: string;
}) {
  const badgeClass = tone === 'warning' ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger';

  return (
    <tr className="hover:bg-subtle align-top">
      <td className="px-3 py-2 text-ink-primary">{r.email}</td>
      <td className="px-3 py-2 text-ink-secondary">{r.name}</td>
      <td className="px-3 py-2">
        <span className={`rounded px-2 py-0.5 text-xs ${badgeClass}`}>{label}</span>
        {r.aiSeverity && (
          <div
            className={`mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
              r.aiSeverity === 'PERMANENT'
                ? 'bg-danger/10 text-danger'
                : 'bg-warning/10 text-warning'
            }`}
          >
            {r.aiSeverity === 'PERMANENT' ? 'Permanente' : 'Temporário'}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-ink-tertiary max-w-md whitespace-pre-wrap break-words">
        {r.errorMessage ?? '—'}
        {r.bouncedAt && (
          <div className="mt-1 text-[10px] text-ink-tertiary/70">
            {new Date(r.bouncedAt).toLocaleString()}
          </div>
        )}
      </td>
      <td className="px-3 py-2 max-w-md">
        {r.aiAnalyzedAt ? (
          <div className="space-y-1">
            {r.aiCategory && (
              <div className="text-[10px] font-mono text-ink-tertiary">{r.aiCategory}</div>
            )}
            {r.aiAction && (
              <div className="text-xs text-ink-primary whitespace-pre-wrap break-words">
                {r.aiAction}
              </div>
            )}
            {r.aiSuggestedEmail && (
              <div className="text-xs text-accent">
                Email provável: <span className="font-mono">{r.aiSuggestedEmail}</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-ink-tertiary/60">— sem análise —</span>
        )}
      </td>
    </tr>
  );
}
